import clsx from 'clsx'
import { ArrowRight, ArrowUpRight } from 'lucide-react'

import Button from '/react4xp/components/prototype/Button'

import ContentBlockVideo from './Video'
import type { ContentBlockBackground, ContentBlockProps } from './types'

const backgroundClasses: Record<ContentBlockBackground, string> = {
	purple: 'bg-brand-200',
	green: 'bg-green-100',
	red: 'bg-red-100',
	beige: 'bg-cream-50',
	white: 'bg-pure-white',
}

const darkDecorationBackgrounds: ContentBlockBackground[] = ['purple', 'green', 'red']

export default function ContentBlock({
	overline,
	heading,
	body,
	image,
	video,
	mediaPosition = 'left',
	link,
	background = 'beige',
}: ContentBlockProps) {
	const bgClass = backgroundClasses[background] ?? backgroundClasses.beige

	const overlineText = overline && <p className="body text-black-950">{overline}</p>

	const bodyText = body && (
		typeof body === 'string' ? (
			<div className="body text-black-950" dangerouslySetInnerHTML={{ __html: body }} suppressHydrationWarning />
		) : (
			<div className="body text-black-950">{body}</div>
		)
	)

	const readMoreLink = link && (
		<Button
			variant="link"
			href={link.url}
			isExternal={link.isExternal}
			className={clsx(
				'w-fit',
				darkDecorationBackgrounds.includes(background) && '!decoration-black-600 hover:!decoration-black-950',
			)}
		>
			{link.label}
			{link.isExternal ? (
				<ArrowUpRight className="h-[1lh] w-5 shrink-0" aria-hidden="true" />
			) : (
				<ArrowRight className="h-[1lh] w-5 shrink-0" aria-hidden="true" />
			)}
		</Button>
	)

	if (image || video) {
		// ── With-media layout ──
		const mediaFirst = mediaPosition === 'left'

		return (
			<section className={`${bgClass} py-12`}>
				<div className="container">
					{/* Media stacks on top below lg for both media positions */}
					<div
						className={`flex flex-col gap-8 lg:flex-row lg:gap-0 lg:items-center lg:justify-between ${mediaFirst ? '' : 'lg:flex-row-reverse'}`}
					>
						{/* ── Media column — interior padding forms the channel to the text ── */}
						<div className={`lg:flex-1 ${mediaFirst ? 'lg:pr-16' : 'lg:pl-16'}`}>
							<figure
								className={clsx(
									'w-full max-w-full aspect-square md:max-h-175 lg:aspect-auto lg:h-130 overflow-hidden',
									!video && 'rounded-sm',
								)}
							>
								{video ? (
									<ContentBlockVideo src={video.src} className="w-full h-full object-cover" />
								) : (
									image && (
										<img src={image.src} alt={image.alt} className="w-full h-full object-cover" />
									)
								)}
							</figure>
						</div>
						{/* ── Text column ── */}
						<div className="flex flex-col gap-6 lg:flex-1 lg:justify-center">
							{overlineText}
							{heading && <h2 className="h2 text-black-950">{heading}</h2>}
							{bodyText}
							{readMoreLink}
						</div>
					</div>
				</div>
			</section>
		)
	}

	// ── No-media layout ──
	return (
		<section className={`${bgClass} py-14`}>
			<div className="container">
				<div className="flex flex-col gap-8 lg:flex-row lg:gap-16 lg:items-start">
					{/* ── Title column ── */}
					<div className="flex flex-col gap-6 lg:w-108.5 lg:shrink-0">
						{overlineText}
						{heading && <h2 className="h3 text-black-950">{heading}</h2>}
					</div>
					{/* ── Text column ── */}
					<div className="flex flex-col gap-8 lg:flex-1 lg:min-w-0 lg:max-w-200">
						{bodyText}
						{readMoreLink}
					</div>
				</div>
			</div>
		</section>
	)
}
